import { NavLink, Outlet, useLocation, useMatch, Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { ExternalLink, LogOut, MoreHorizontal, X } from 'lucide-react';
import {
  ADMIN_MOBILE_MORE_ANCHORS,
  ADMIN_MOBILE_PRIMARY_ANCHORS,
  ADMIN_SIDEBAR_ANCHORS,
  ADMIN_SIDEBAR_GROUPS,
} from './spaces';
import { useOverlayScrollLock } from '../hooks/useOverlayScrollLock';
import { useAuth } from '../state/AuthContext';
import { getTournament, getTournamentBySlug } from '../services/tournamentService';
import { useStore } from '../hooks/useStore';
import { paths } from '../lib/paths';
import { ThemeToggle } from './ThemeToggle';
import { ScorekeeperIcon } from './ScorekeeperLogo';

/**
 * Admin chrome for the customer-facing League HQ.
 *
 * - Web ≥960px: full sidebar + topbar.
 * - 640–960px: sidebar narrows to icon-only (CSS handles it).
 * - <640px: bottom-tab nav, sidebar hidden, topbar shrinks.
 *
 * Identity in the footer reflects the real Google OAuth session.
 */
export function AdminShell() {
  const { canAdmin, googleEmail, isSignedIn, publicHqAccess } = useAuth();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  useOverlayScrollLock(moreOpen);
  const league = useStore((d) => d.league);
  const orgName = league.name;

  // If we're inside a competition workspace, surface its name + a public-link button.
  const tournamentWorkspaceMatch = useMatch('/admin/tournaments/:id/*');
  const seasonWorkspaceMatch = useMatch('/admin/seasons/:id/*');
  const workspaceMatch = tournamentWorkspaceMatch ?? seasonWorkspaceMatch;
  const tournamentId = workspaceMatch?.params.id;
  const tournament = useStore(() => {
    if (!tournamentId) return undefined;
    return getTournament(tournamentId) ?? getTournamentBySlug(tournamentId);
  });

  const activeAnchor = useMemo(() => {
    return ADMIN_SIDEBAR_ANCHORS.find((a) =>
      a.end ? location.pathname === a.path : location.pathname.startsWith(a.path),
    );
  }, [location.pathname]);

  const topbarTitle = tournament
    ? tournament.name
    : activeAnchor?.label ?? 'Dashboard';
  const topbarHint = tournament
    ? `${tournament.status.replace('_', ' ')} · ${new Date(tournament.startsAt).toLocaleDateString()}`
    : '';
  const accountLabel = googleEmail ?? (publicHqAccess ? 'Public QA access' : 'Not signed in');

  // Group sidebar anchors by lifecycle group.
  const groupedAnchors = useMemo(() => {
    const groups: Record<'run' | 'grow', typeof ADMIN_SIDEBAR_ANCHORS> = {
      run: [],
      grow: [],
    };
    for (const a of ADMIN_SIDEBAR_ANCHORS) groups[a.group].push(a);
    return groups;
  }, []);

  const moreActive = ADMIN_MOBILE_MORE_ANCHORS.some((a) => location.pathname === a.path || location.pathname.startsWith(`${a.path}/`));

  return (
    <div className="app-shell">
      <aside className="app-shell__sidebar" aria-label="Primary navigation">
        <Link to={paths.adminHome} className="brand" aria-label="League HQ home">
          <div className={`brand__mark ${league.branding.logoAssetId ? '' : 'brand__mark--asset'}`} aria-hidden>
            {league.branding.logoAssetId ? (
              <img
                src={`/api/public-asset?id=${encodeURIComponent(league.branding.logoAssetId)}`}
                alt=""
                style={{ width: 24, height: 24, objectFit: 'contain', borderRadius: 6 }}
              />
            ) : (
              <ScorekeeperIcon />
            )}
          </div>
          <div className="brand__text">
            <div className="brand__name">{orgName}</div>
            <div className="brand__sub">League HQ</div>
          </div>
        </Link>

        <nav className="nav" aria-label="Sections">
          {(['run', 'grow'] as const).map((group) => (
            <div key={group}>
              <div className="nav__group-label">{ADMIN_SIDEBAR_GROUPS[group]}</div>
              {groupedAnchors[group].map((a) => (
                <NavLink
                  key={a.path}
                  to={a.path}
                  end={a.end}
                  className={({ isActive }) => `nav__item ${isActive ? 'is-active' : ''}`}
                >
                  <a.icon className="nav__icon" />
                  <span>{a.label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          <div className="nav__group-label" style={{ marginTop: 18 }}>Preview</div>
          <NavLink
            to={paths.events}
            className={({ isActive }) => `nav__item ${isActive ? 'is-active' : ''}`}
          >
            <ExternalLink className="nav__icon" />
            <span>Public site</span>
          </NavLink>

        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-footer__label">Signed in as</div>
          <div className="sidebar-account">
            <div className="avatar" aria-hidden>
              {accountLabel[0]?.toUpperCase()}
            </div>
            <div className="sidebar-account__meta">
              <div
                className="sidebar-account__email"
                title={accountLabel}
              >
                {accountLabel}
              </div>
              <div className="sidebar-account__role">
                {publicHqAccess ? 'Testing access' : canAdmin ? 'Admin' : 'Member'}
              </div>
            </div>
          </div>
          {isSignedIn && (
            <a href="/api/auth/logout?return_to=/" className="btn btn--ghost btn--sm sidebar-footer__signout">
              <LogOut size={14} /> Sign out
            </a>
          )}
        </div>
      </aside>

      <header className="app-shell__topbar">
        <div className="topbar__title">
          <h1>{topbarTitle}</h1>
          {topbarHint && <span className="muted" style={{ fontSize: 12 }}>{topbarHint}</span>}
        </div>
        <div className="topbar__spacer" />
        <ThemeToggle />
        {tournament && (
          <NavLink
            to={paths.event(tournament.slug)}
            className="btn btn--ghost btn--sm"
            style={{ marginRight: 4 }}
          >
            <ExternalLink size={14} /> Public page
          </NavLink>
        )}
      </header>

      <main className="app-shell__main">
        <div className="page">
          <Outlet />
        </div>
      </main>

      <nav className="bottom-nav" aria-label="Mobile navigation">
        <ul className="bottom-nav__list">
          {ADMIN_MOBILE_PRIMARY_ANCHORS.map((a) => (
            <li key={a.path} style={{ flex: 1 }}>
              <NavLink
                to={a.path}
                end={a.end}
                onClick={() => setMoreOpen(false)}
                className={({ isActive }) =>
                  `bottom-nav__item ${isActive || (a.hash && location.hash === `#${a.hash}`) ? 'is-active' : ''}`
                }
              >
                <a.icon size={20} />
                <span>{a.label}</span>
              </NavLink>
            </li>
          ))}
          <li style={{ flex: 1 }}>
            <button
              type="button"
              className={`bottom-nav__item ${moreOpen || moreActive ? 'is-active' : ''}`}
              onClick={() => setMoreOpen((open) => !open)}
              aria-expanded={moreOpen}
              aria-controls="admin-more-sheet"
            >
              <MoreHorizontal size={20} />
              <span>More</span>
            </button>
          </li>
        </ul>
      </nav>

      {moreOpen && (
        <div
          className="sheet admin-more-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="More admin sections"
          id="admin-more-sheet"
          onClick={() => setMoreOpen(false)}
        >
          <div className="sheet__panel admin-more-sheet__panel overlay-panel" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__head overlay-head">
              <div>
                <div className="eyebrow">More</div>
                <div style={{ fontWeight: 700 }}>Admin sections</div>
              </div>
              <button className="btn btn--ghost btn--sm" onClick={() => setMoreOpen(false)} aria-label="Close menu">
                <X size={14} />
              </button>
            </div>
            <div className="sheet__body admin-more-sheet__body overlay-body touch-scroll">
              {ADMIN_MOBILE_MORE_ANCHORS.map((a) => (
                <NavLink
                  key={a.path}
                  to={a.path}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) => `sheet__item admin-more-sheet__item ${isActive ? 'is-active' : ''}`}
                >
                  <a.icon size={18} />
                  <span>{a.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
